import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  ChamberMemberProPaymentScheduleEnum,
  CmpMemberLifecycleStatusEnum,
  GetCmpManageMembersQuery,
  useArchiveCmpMemberMutation,
  useGetCmpManageMembersQuery,
  useGetCmpMemberDeleteCheckQuery,
} from "../../../../graphql/types";
import { CMP_MEMBER_STATUS_OPTIONS, cmpMemberStatusLabels } from "../memberStatusLabels";
import { RoutePaths } from "../../../../enumStrings";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import {
  cpaGold,
  cpaGoldDark,
  memberRequestApolloContext,
} from "../memberWorkflowShared";
import { getMembershipTypeDisplayLabel } from "../memberProfileMembership";
import MemberPastTransitionPaymentSurfaceFeedback, {
  getMemberPastTransitionPaymentSurfaceFeedback,
  MemberPastTransitionPaymentSurfaceFeedbackState,
} from "../MemberPastTransitionPaymentSurfaceFeedback";
import MemberActionSuccessDialog from "../MemberActionSuccessDialog";
import { refreshAfterCommittedMemberAction } from "../memberInvoiceDelivery";
import { memberPastTransitionApolloContext } from "../memberPastTransitionErrors";
import { TableEmptyState } from "../../../common/dashboard/TableEmptyState";
import { getUtcCalendarDateStartUtcMilli } from "../../../common/dashboard/formatters";
import {
  DataTable,
  DataTableColumn,
} from "../../../common/dashboard/DataTable";
import SearchableSelectField from "../../../common/dashboard/SearchableSelectField";
import DateRangeFilterField from "../../../common/dashboard/DateRangeFilterField";
import DesignDialog, {
  DesignDialogButton,
} from "../../../common/dashboard/DesignDialog";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import {
  ManagePageSection,
  ManagePageShell,
} from "../../../common/dashboard/ManagePageShell";
import {
  sortManageMembers,
} from "../manageMembersSorting";
import MassMemberCredentials from "./MassMemberCredentials";
import { filterManageMembers } from "./manageMembersFilters";
import MemberStatusReasonField from "../MemberStatusReasonField";
import { AutoRenewDeleteWarning, DeleteMemberBlockedReason, DeleteMemberMessage, shouldWarnAutoRenewOnDelete } from "../MemberDeleteNotices";
import {
  getUserFacingErrorText,
  STAFF_SUPPORT_EMAIL,
} from "../../../../utils/userFacingError";

type SortDirection = "asc" | "desc";

type SortableColumnKey =
  | "companyName"
  | "memberStatus"
  | "lastUpdated"
  | "dateJoined"
  | "membershipType"
  | "memberSize"
  | "renewalDate";

type ManageMembersColumnKey = SortableColumnKey | "actions";

type BackendMemberRow =
  GetCmpManageMembersQuery["getCmpManageMembers"]["rows"][number];

type ManageMemberRow = {
  dateJoined: string;
  dateJoinedUtcMilli?: number | null;
  fullTimeEmployees?: number | null;
  id: string;
  lifecycleStatus: CmpMemberLifecycleStatusEnum;
  /** IF11 MB8: derived status where the status model is on, else the lifecycle. */
  statusKey: string;
  memberStatus: string;
  membershipType: string;
  paymentSchedule?: ChamberMemberProPaymentScheduleEnum | null;
  partTimeEmployees?: number | null;
  lastUpdated: string;
  lastUpdatedUtcMilli?: number | null;
  renewalBlockedReason?: string | null;
  billingAddressMissingReason?: string | null;
  renewalDate: string;
  renewalDateUtcMilli?: number | null;
  searchText: string;
  showInApp: boolean;
  companyName: string;
  numberedCompany: string;
};

type AnalyticsMetricCard = {
  currentValue: number;
  formatter?: (value: number) => string;
  label: string;
  positiveChangeIsGood: boolean;
  previousValue: number;
};

type StatusMessage = {
  severity: "success" | "info" | "error";
  message: string;
};

type ManageMembersLocationState = {
  statusMessage?: StatusMessage;
};

const todayInputValue = () => new Date().toISOString().slice(0, 10);

const tableActionButtonSx = {
  borderRadius: "8px",
  boxShadow: "none",
  fontSize: 12.5,
  fontWeight: 600,
  justifyContent: "center",
  lineHeight: "normal",
  padding: "8px 12px",
  textTransform: "none",
  whiteSpace: "nowrap",
  width: "100%",
  "& .MuiButton-startIcon": {
    marginLeft: 0,
    marginRight: 0.75,
    "& svg": { fontSize: 14 },
  },
  "&:hover": { boxShadow: "none" },
};
const pageHeaderActionButtonSx = {
  backgroundColor: cpaGold,
  borderRadius: "8px",
  color: "white",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "normal",
  padding: "12px 20px",
  textTransform: "none",
  whiteSpace: "nowrap",
  boxShadow: "none",
  "& .MuiButton-startIcon svg": { fontSize: 16 },
  "&:hover": { backgroundColor: cpaGoldDark, boxShadow: "none" },
};
const allStatuses: CmpMemberLifecycleStatusEnum[] = [
  CmpMemberLifecycleStatusEnum.Active,
  CmpMemberLifecycleStatusEnum.Pending,
  CmpMemberLifecycleStatusEnum.Past,
  CmpMemberLifecycleStatusEnum.Suspended,
];

const statusLabels: Record<CmpMemberLifecycleStatusEnum, string> = {
  [CmpMemberLifecycleStatusEnum.Active]: "Active",
  [CmpMemberLifecycleStatusEnum.Deleted]: "Past",
  [CmpMemberLifecycleStatusEnum.Past]: "Past",
  [CmpMemberLifecycleStatusEnum.Pending]: "Pending",
  [CmpMemberLifecycleStatusEnum.Prospect]: "Prospect",
  [CmpMemberLifecycleStatusEnum.Suspended]: "Suspended",
};

const BILLING_ADDRESS_MISSING_FILTER_VALUE = "billingAddressMissing";
const billingAddressFilterOptions = [
  {
    value: BILLING_ADDRESS_MISSING_FILTER_VALUE,
    label: "Missing billing address",
  },
];

const currencyFormatter = new Intl.NumberFormat("en-CA", {
  currency: "CAD",
  style: "currency",
});

const integerFormatter = new Intl.NumberFormat("en-CA", {
  maximumFractionDigits: 0,
});

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

const centsToDollars = (amountInCents?: number | null) =>
  amountInCents === undefined || amountInCents === null
    ? 0
    : amountInCents / 100;

const formatCurrency = (amount: number) => currencyFormatter.format(amount);

const formatInteger = (amount: number) =>
  integerFormatter.format(Math.round(amount));

const formatDate = (date?: number | string | null) => {
  if (!date) {
    return "-";
  }

  return dateFormatter.format(
    typeof date === "number" ? new Date(date) : new Date(`${date}T00:00:00Z`),
  );
};

const toDisplayMember = (row: BackendMemberRow): ManageMemberRow => {
  return {
    companyName: row.companyName,
    numberedCompany: row.numberedCompany || "",
    dateJoined: formatDate(row.dateJoinedUtcMilli),
    dateJoinedUtcMilli: row.dateJoinedUtcMilli,
    fullTimeEmployees: row.fullTimeEmployees,
    id: row.id,
    lifecycleStatus: row.lifecycleStatus,
    statusKey: row.memberStatus || row.lifecycleStatus,
    memberStatus: row.memberStatus ? cmpMemberStatusLabels[row.memberStatus] : statusLabels[row.lifecycleStatus],
    membershipType: getMembershipTypeDisplayLabel(row.membershipTypeName),
    partTimeEmployees: row.partTimeEmployees,
    paymentSchedule: row.paymentSchedule,
    showInApp: row.showInApp,
    lastUpdated: formatDate(row.lastUpdatedUtcMilli),
    lastUpdatedUtcMilli: row.lastUpdatedUtcMilli,
    renewalBlockedReason: row.renewalBlockedReason,
    billingAddressMissingReason: row.billingAddressMissingReason,
    renewalDate: formatDate(row.renewalDateUtcMilli),
    renewalDateUtcMilli: row.renewalDateUtcMilli,
    // The design search also matches the status word (CD:15558).
    searchText: row.memberStatus ? `${row.searchText} ${cmpMemberStatusLabels[row.memberStatus]}` : row.searchText,
  };
};

const getMemberSortValue = (
  member: ManageMemberRow,
  column: SortableColumnKey,
): string | number => {
  switch (column) {
    case "memberStatus":
      return member.memberStatus;
    case "lastUpdated":
      return member.lastUpdatedUtcMilli || 0;
    case "dateJoined":
      return member.dateJoinedUtcMilli || 0;
    case "membershipType":
      return member.membershipType;
    case "memberSize":
      return member.fullTimeEmployees || 0;
    case "renewalDate":
      return member.renewalDateUtcMilli || 0;
    case "companyName":
    default:
      return member.companyName;
  }
};

const getMemberSizeLines = (member: ManageMemberRow) => {
  const fullTimeEmployees = member.fullTimeEmployees || 0;
  const partTimeEmployees = member.partTimeEmployees || 0;

  // The design always shows both counts, with 0 when a count is missing.
  return [
    `${formatInteger(fullTimeEmployees)} Full Time`,
    `${formatInteger(partTimeEmployees)} Part Time`,
  ];
};

// A blocked renewal is a real failure, so it wins over the billing address
// notice. A missing billing address does not stop the renewal invoice.
const getRenewalDateMarker = (member: ManageMemberRow) => {
  if (member.renewalBlockedReason) {
    return {
      className: "text-[#D32F2F]",
      text: "Renewal blocked",
      title: member.renewalBlockedReason,
    };
  }
  if (member.billingAddressMissingReason) {
    return {
      className: "text-amber-700",
      text: "Billing address missing",
      title: member.billingAddressMissingReason,
    };
  }
  return null;
};

const getTrend = ({
  currentValue,
  positiveChangeIsGood,
  previousValue,
}: Pick<
  AnalyticsMetricCard,
  "currentValue" | "positiveChangeIsGood" | "previousValue"
>) => {
  if (previousValue === currentValue) {
    return {
      className: "text-[#727272]",
      text: "No change from last year",
    };
  }

  if (previousValue === 0) {
    return {
      className: positiveChangeIsGood ? "text-[#10B981]" : "text-[#F21B1B]",
      text: "New this year",
    };
  }

  const percentChange = Math.round(
    (Math.abs(currentValue - previousValue) / Math.abs(previousValue)) * 100,
  );
  const increased = currentValue >= previousValue;
  const isGoodChange = increased ? positiveChangeIsGood : !positiveChangeIsGood;

  return {
    className: isGoodChange ? "text-[#10B981]" : "text-[#F21B1B]",
    text: `${percentChange}% ${increased ? "Increase" : "decrease"} from last year`,
  };
};

const ManageMembers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const chamber = useGetChamberFromUserIdInAuth();
  const noun = useCmpOrganizationNoun();
  const chamberId = chamber?.id || "";
  const [archiveSuccessOpen, setArchiveSuccessOpen] = useState(false);
  const [archivedMemberName, setArchivedMemberName] = useState("");
  const [archiveError, setArchiveError] = useState("");
  // CMP Dashboard (19) L15347: the row Delete uses the same required reason.
  const [archiveReason, setArchiveReason] = useState("");
  const [archiveReasonError, setArchiveReasonError] = useState(false);
  const [archivePaymentSurfaceFeedback, setArchivePaymentSurfaceFeedback] =
    useState<MemberPastTransitionPaymentSurfaceFeedbackState | null>(null);
  const [archiveRefreshWarning, setArchiveRefreshWarning] = useState<
    string | undefined
  >(undefined);
  const [archiveRefreshLoading, setArchiveRefreshLoading] = useState(false);
  const [memberToArchive, setMemberToArchive] =
    useState<ManageMemberRow | null>(null);
  const [renewalDateFrom, setRenewalDateFrom] = useState("");
  const [renewalDateTo, setRenewalDateTo] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedBillingAddressFilters, setSelectedBillingAddressFilters] =
    useState<string[]>([]);
  const billingAddressMissingOnly = selectedBillingAddressFilters.includes(
    BILLING_ADDRESS_MISSING_FILTER_VALUE,
  );
  const [sortColumn, setSortColumn] = useState<SortableColumnKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(
    null,
  );
  const manageMembersQueryVariables = useMemo(
    () => ({
      input: {
        chamberId,
        lifecycleStatuses: allStatuses,
        // IF11 MB8: every status where the status model is on (the design
        // list opens with no status filter); ignored for other chambers.
        memberStatuses: [],
        // The backend owns the billing address check, so this filter runs
        // server side. Leave the field out when the filter is off.
        ...(billingAddressMissingOnly
          ? { billingAddressMissingOnly: true }
          : {}),
      },
    }),
    [billingAddressMissingOnly, chamberId],
  );

  const {
    data: manageMembersData,
    loading: manageMembersLoading,
    error: manageMembersError,
    refetch: refetchManageMembers,
  } = useGetCmpManageMembersQuery({
    context: memberRequestApolloContext,
    variables: manageMembersQueryVariables,
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });
  const [archiveCmpMember, { loading: archivingMember }] =
    useArchiveCmpMemberMutation({
      context: memberPastTransitionApolloContext,
    });
  // Monday 13389365001 (S9), CMP Dashboard (24) CD:16996: the row Delete
  // pop-up shows the same auto-renewal warning and refusal reason as Update
  // Member. A failed read changes nothing: the delete is checked again on save.
  const { data: deleteCheckData } = useGetCmpMemberDeleteCheckQuery({
    context: memberRequestApolloContext,
    variables: {
      input: { chamberId: chamberId || "", memberId: memberToArchive?.id || "" },
    },
    skip: !chamberId || !memberToArchive,
    fetchPolicy: "no-cache",
  });
  const deleteCheck =
    memberToArchive &&
    deleteCheckData?.getCmpMemberDetail.member.id === memberToArchive.id
      ? deleteCheckData.getCmpMemberDetail
      : null;
  const deleteBlockedReason =
    deleteCheck && !deleteCheck.statusActions.canArchive
      ? deleteCheck.statusActions.archiveBlockedReason
      : null;
  const deleteAutoRenewWarning =
    !!deleteCheck &&
    shouldWarnAutoRenewOnDelete({
      autoRenewEnabled: deleteCheck.renewal?.autoRenewEnabled,
      lifecycleStatus: deleteCheck.lifecycleStatus,
      memberStatus: deleteCheck.memberStatus,
    });
  const allMembers = useMemo(
    () =>
      (manageMembersData?.getCmpManageMembers.rows || []).map(toDisplayMember),
    [manageMembersData],
  );
  // IF11 MB8: the 7 design statuses where the status model is on (Sand Bay first).
  const memberStatusModel = !!manageMembersData?.getCmpManageMembers.memberStatusModel;
  const statusFilterOptions: { value: string; label: string }[] = memberStatusModel
    ? CMP_MEMBER_STATUS_OPTIONS.map((status) => ({ value: status, label: cmpMemberStatusLabels[status] }))
    : allStatuses.map((status) => ({ value: status, label: statusLabels[status] }));
  const members = useMemo(() => {
    const statusSearchAndRenewalFilteredMembers = filterManageMembers({
      members: allMembers,
      search: searchQuery,
      statuses: selectedStatuses,
      renewalDateFrom,
      renewalDateTo,
    });
    if (!sortColumn) return statusSearchAndRenewalFilteredMembers;
    return sortManageMembers({
      column: sortColumn,
      direction: sortDirection,
      getSortValue: getMemberSortValue,
      members: statusSearchAndRenewalFilteredMembers,
    });
  }, [
    allMembers,
    renewalDateFrom,
    renewalDateTo,
    searchQuery,
    selectedStatuses,
    sortColumn,
    sortDirection,
  ]);
  const analytics = manageMembersData?.getCmpManageMembers.analytics;
  const analyticsCards: AnalyticsMetricCard[] = analytics
    ? [
        {
          currentValue: analytics.totalMembersYtd,
          // The backend returns the member count as at today in the current
          // fiscal year (previous year: as at the same point last year).
          label: "Total Number of Member YTD",
          positiveChangeIsGood: true,
          previousValue: analytics.previousFiscalYearTotalMembersYtd,
        },
        {
          currentValue: analytics.totalNewMembersYtd,
          label: "Total New Members YTD",
          positiveChangeIsGood: true,
          previousValue: analytics.previousFiscalYearNewMembersYtd,
        },
        {
          currentValue: analytics.totalMemberCancellationsYtd,
          label: "Total Member Cancellations YTD",
          positiveChangeIsGood: false,
          previousValue: analytics.previousFiscalYearMemberCancellationsYtd,
        },
        {
          currentValue: analytics.totalSuspendedMembersYtd,
          // The backend returns the current suspended count for this year.
          // A count of suspensions within the fiscal year needs a backend field.
          label: "Total Suspended Members YTD",
          positiveChangeIsGood: false,
          previousValue: analytics.previousFiscalYearSuspendedMembersYtd,
        },
        {
          currentValue: analytics.totalNewProspectMembersYtd,
          label: "Total New Prospect Member YTD",
          positiveChangeIsGood: true,
          previousValue: analytics.previousFiscalYearNewProspectMembersYtd,
        },
        {
          currentValue: centsToDollars(
            analytics.totalMembershipRevenueYtdInCents,
          ),
          formatter: formatCurrency,
          label: "Total Membership Revenue YTD",
          positiveChangeIsGood: true,
          previousValue: centsToDollars(
            analytics.previousFiscalYearMembershipRevenueYtdInCents,
          ),
        },
      ]
    : [];
  useEffect(() => {
    const incomingStatusMessage = (
      location.state as ManageMembersLocationState | null
    )?.statusMessage;

    if (!incomingStatusMessage) {
      return;
    }

    setStatusMessage(incomingStatusMessage);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const requestSort = (column: SortableColumnKey) => {
    if (column !== sortColumn) {
      setSortColumn(column);
      setSortDirection("asc");
    } else if (sortDirection === "asc") setSortDirection("desc");
    else {
      setSortColumn(null);
      setSortDirection("asc");
    }
  };

  const clearFilters = () => {
    setRenewalDateFrom("");
    setRenewalDateTo("");
    setSearchQuery("");
    setSelectedStatuses([]);
    setSelectedBillingAddressFilters([]);
    setSortColumn(null);
    setSortDirection("asc");
    setStatusMessage(null);
  };

  const handleArchiveClick = (member: ManageMemberRow) => {
    setStatusMessage(null);
    setArchivePaymentSurfaceFeedback(null);
    setArchiveRefreshWarning(undefined);
    setArchiveError("");
    setArchiveReason("");
    setArchiveReasonError(false);
    setMemberToArchive(member);
  };

  const handleRetryArchiveRefresh = async () => {
    setArchiveRefreshLoading(true);
    const refreshWarning =
      await refreshAfterCommittedMemberAction(refetchManageMembers);
    setArchiveRefreshWarning(refreshWarning);
    setArchiveRefreshLoading(false);
  };

  const handleArchiveConfirm = async () => {
    if (!chamberId || !memberToArchive) {
      return;
    }
    const reason = archiveReason.trim();
    if (!reason) {
      setArchiveReasonError(true);
      return;
    }

    try {
      const result = await archiveCmpMember({
        variables: {
          input: {
            archivedAtUtcMilli:
              getUtcCalendarDateStartUtcMilli(todayInputValue()),
            archiveReason: reason,
            chamberId,
            memberId: memberToArchive.id,
          },
        },
      });
      if (!result.data?.archiveCmpMember.actionSuccessful)
        throw new Error(
          "The member was not deleted. Refresh the member before trying again.",
        );
      setArchivedMemberName(memberToArchive.companyName);
      setArchivePaymentSurfaceFeedback(
        getMemberPastTransitionPaymentSurfaceFeedback(
          result.data?.archiveCmpMember.detail.invoices,
        ),
      );
      setMemberToArchive(null);
      setArchiveSuccessOpen(true);
      setArchiveRefreshWarning(
        await refreshAfterCommittedMemberAction(refetchManageMembers),
      );
    } catch (error) {
      const archiveErrorText = getUserFacingErrorText(
        error,
        `${memberToArchive.companyName} was not deleted. Close this window, refresh the page, and try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`,
      );
      setArchiveError(archiveErrorText);
      setStatusMessage({
        severity: "error",
        message: archiveErrorText,
      });
    }
  };

  // Page-header action buttons live in the PageContainer header zone (same
  // pattern as Manage Invoices) instead of the Member Lists section header.
  const pageHeaderActions = useMemo(
    () => (
      <div className="flex flex-wrap justify-end gap-[12px]">
        <Button
          type="button"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(RoutePaths.chamberAdmin_members_create)}
          sx={pageHeaderActionButtonSx}
        >
          Add New Member
        </Button>
      </div>
    ),
    [navigate],
  );

  const headerOverride = useMemo(
    () => ({
      title: "Manage Members",
      description:
        "Use this section to manage and update member information, including membership type, renewal dates, employee count and member status. Send Member Dashboard credentials, reset passwords, and view member insights, activity and communications.",
      titleStyle: { fontSize: 28, fontWeight: 700, lineHeight: 1.25, color: "#0A0A0A" },
      descriptionStyle: { fontSize: 14, lineHeight: "normal" },
      descriptionClassName: "mt-[6px] text-[#0A0A0A]",
      containerClassName: "members-manage-page font-['DM_Sans',sans-serif]",
      horizontalPaddingClassName: "px-[16px] md:px-[40px]",
      // Offset the shared desktop layout padding to keep the 32px design inset.
      headerSpacingClassName: "pt-[32px] pb-0 md:pt-[calc(32px-1rem)]",
      actions: pageHeaderActions,
    }),
    [pageHeaderActions],
  );

  const columns: DataTableColumn<ManageMemberRow, ManageMembersColumnKey>[] = [
    {
      key: "companyName",
      header: "Member Company",
      sortable: true,
      render: (member) => {
        return (
          <div className="flex flex-col gap-[2px]">
            <Link
              className="text-[12px] font-medium text-[#0A0A0A] underline"
              to={RoutePaths.chamberAdmin_members_updateLegacy.replace(
                ":memberId",
                encodeURIComponent(member.id),
              )}
            >
              {member.companyName}
            </Link>
            {member.numberedCompany ? (
              <span className="text-[12px] text-[#727272]">{member.numberedCompany}</span>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "memberStatus",
      header: "Member Status",
      sortable: true,
      render: (member) => (
        <span className="text-[12px] text-[#0A0A0A]">
          {member.memberStatus}
        </span>
      ),
    },
    {
      key: "dateJoined",
      header: "Date Joined",
      sortable: true,
      render: (member) => member.dateJoined,
    },
    {
      key: "membershipType",
      header: "Membership Type",
      sortable: true,
      render: (member) => member.membershipType,
    },
    {
      key: "memberSize",
      header: "Member Size",
      sortable: true,
      render: (member) => (
        <div className="flex flex-col gap-[2px]">
          {getMemberSizeLines(member).map((line) => (
            <span key={line}>{line}</span>
          ))}
        </div>
      ),
    },
    {
      key: "renewalDate",
      header: "Renewal Date",
      sortable: true,
      // The full reason and the fix are on the member page.
      render: (member) => {
        const marker = getRenewalDateMarker(member);
        return marker ? (
          <div className="flex flex-col gap-[2px]">
            <span>{member.renewalDate}</span>
            <span
              className={`text-[12px] font-semibold ${marker.className}`}
              title={marker.title}
            >
              {marker.text}
            </span>
          </div>
        ) : member.renewalDate;
      },
    },
    {
      key: "lastUpdated",
      header: "Last Updated",
      sortable: true,
      render: (member) => member.lastUpdated,
    },
    {
      key: "actions",
      header: "Actions",
      headerCellSx: { fontWeight: 700 },
      cellSx: { padding: "12px 14px !important" },
      stickyRightSx: {
        borderLeft: 0,
        boxShadow: {
          xs: "none",
          md: "-6px 0 8px -6px rgba(0,0,0,.12)",
        },
        position: { xs: "static", md: "sticky" },
      },
      stickyRight: true,
      render: (member) => {
        // CMP Dashboard (24) CD:7295-7296, CD:17001-17004: Deleted and Past
        // rows have no Delete; their one button (View on Deleted) is centred
        // in the height of the two-button cell.
        const noDelete = member.memberStatus === "Deleted" || member.memberStatus === "Past";
        const viewOnly = member.memberStatus === "Deleted";
        const openButton = (
          <Button
            component={Link}
            to={RoutePaths.chamberAdmin_members_updateLegacy.replace(
              ":memberId",
              encodeURIComponent(member.id),
            )}
            size="small"
            variant="contained"
            sx={{
              ...tableActionButtonSx,
              width: "100%",
              backgroundColor: viewOnly ? "#0A0A0A" : cpaGold,
              color: "white",
              "&:hover": {
                backgroundColor: viewOnly ? "#2C2C2C" : cpaGoldDark,
              },
            }}
          >
            {viewOnly ? "View" : "Update"}
          </Button>
        );
        if (noDelete) {
          return (
            <div className="grid grid-cols-[minmax(0,1fr)]">
              {/* Invisible copies of the two buttons keep the cell height exact. */}
              <div aria-hidden="true" className="invisible flex flex-col gap-[8px] [grid-area:1/1]">
                <Button tabIndex={-1} size="small" variant="contained" sx={tableActionButtonSx}>{"\u200b"}</Button>
                <Button tabIndex={-1} size="small" variant="contained" sx={tableActionButtonSx}>{"\u200b"}</Button>
              </div>
              <div className="self-center [grid-area:1/1]">{openButton}</div>
            </div>
          );
        }
        return (
        <div className="flex flex-col items-stretch gap-[8px]">
          {openButton}
          <Button
            type="button"
            size="small"
            variant="contained"
            color="error"
            onClick={() => handleArchiveClick(member)}
            sx={{
              ...tableActionButtonSx,
              backgroundColor: "#F21B1B",
              "&:hover": { backgroundColor: "#D90F1A" },
            }}
          >
            Delete
          </Button>
        </div>
        );
      },
    },
  ];

  return (
    <ManagePageShell
      className="members-manage-design w-full pb-[40px]"
      headerOverride={headerOverride}
      statusMessage={statusMessage}
      onStatusMessageClose={() => setStatusMessage(null)}
    >
      <style>{`
        .members-manage-page .MuiButton-root, .members-manage-page .MuiTableCell-root { font-family: inherit; }
        .members-manage-page > div:nth-child(2) { padding-top: 0; }
        .members-manage-page > div:first-child > div { gap: 24px; flex-wrap: wrap; padding-bottom: 16px; border-bottom: 1px solid #ECECEC; }
        .members-manage-page > div:first-child > div > div:first-child { flex: 1; min-width: 280px; }
        .members-manage-design .cmp-dashboard-density-manage-description { font-size: 14px !important; }
        .members-manage-design .cmp-dashboard-density-data-table .MuiTableCell-root { font-size: 12px !important; }
        .members-manage-design .cmp-dashboard-density-data-table .MuiTableHead-root .MuiTableCell-root { font-size: 13px !important; }
      `}</style>
      <ManagePageSection
        className="pt-[24px]"
        title="Total Member Analytics (Fiscal Year to Date)"
        titleClassName="text-[17px] font-bold leading-[normal] text-[#0A0A0A]"
        description={`See a quick overview of your ${noun.singular} total year to date membership analytics at a glance.`}
        descriptionClassName="mt-[6px] text-[14px] leading-[normal] text-black"
      >
        <div className="mt-[24px] grid auto-rows-auto grid-cols-[repeat(auto-fit,minmax(210px,1fr))] gap-[12px]">
          {analyticsCards.length > 0 ? (
            analyticsCards.map((card) => {
              const trend = getTrend(card);

              return (
                <article
                  key={card.label}
                  className="row-span-3 grid grid-cols-[minmax(0,1fr)] grid-rows-subgrid content-start gap-y-[12px] rounded-[8px] border border-[#ECECEC] px-[18px] py-[16px] shadow-[0_1px_2px_rgba(0,0,0,.04)]"
                >
                  <h3 className="text-[14px] font-normal leading-[normal] text-[#0A0A0A]">
                    {card.label}
                  </h3>
                  <p className="self-end whitespace-nowrap text-[18px] font-bold leading-[normal] text-[#0A0A0A]">
                    {card.formatter
                      ? card.formatter(card.currentValue)
                      : formatInteger(card.currentValue)}
                  </p>
                  <p
                    className={`self-end text-[12px] font-medium leading-[normal] ${trend.className}`}
                  >
                    {trend.text}
                  </p>
                </article>
              );
            })
          ) : (
            <div className="col-span-full flex items-center gap-3 rounded-[8px] border border-gray-200 p-5 text-[14px] font-semibold text-gray-500">
              {manageMembersError ? (
                "We could not load member analytics. Click Retry below, or refresh the page."
              ) : (
                <>
                  <CircularProgress size={20} />
                  Loading fiscal year analytics...
                </>
              )}
            </div>
          )}
        </div>
      </ManagePageSection>

      <ManagePageSection
        className="mt-[24px]"
        title="Member Filters"
        titleClassName="text-[17px] font-bold leading-[normal] text-[#0A0A0A]"
        description="Search members name or contact details (e.g.: Member Name, Email, Phone Number)"
        descriptionClassName="mt-[6px] text-[14px] leading-[normal] text-black"
      >
        <div className="mt-[24px] flex flex-wrap items-center gap-[14px]">
          <div className="relative min-w-[280px] flex-[1_1_280px]">
            <SearchIcon
              sx={{
                position: "absolute",
                left: 14,
                top: 14,
                fontSize: 16,
                color: "#9E9E9E",
              }}
            />
            <input
              aria-label="Search members"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Enter member name, numbered company or member details"
              className="w-full rounded-[8px] border border-[#ECECEC] bg-white py-[12px] pl-[40px] pr-[14px] text-[14px] leading-[normal]"
            />
          </div>
          <div className="w-full md:w-[250px] md:shrink-0">
            <SearchableSelectField
              id="member-status-filter"
              multiple
              selectionDisplay="summary"
              options={statusFilterOptions}
              value={selectedStatuses}
              onChange={(statuses) =>
                setSelectedStatuses(statusFilterOptions.map(({ value }) => value).filter((status) => statuses.includes(status)))
              }
              placeholder="Filter by Member Status"
              searchPlaceholder="Search Member Status"
              leadingIcon={<TuneIcon sx={{ fontSize: 17 }} />}
            />
          </div>
          <div className="w-full md:w-[250px] md:shrink-0">
            <DateRangeFilterField
              from={renewalDateFrom}
              to={renewalDateTo}
              onChange={({ from, to }) => {
                setRenewalDateFrom(from);
                setRenewalDateTo(to);
              }}
              label="Filter by Renewal Date"
              title="Renewal Date Range"
            />
          </div>
          <div className="w-full md:w-[250px] md:shrink-0">
            <SearchableSelectField
              id="member-billing-address-filter"
              multiple
              selectionDisplay="summary"
              options={billingAddressFilterOptions}
              value={selectedBillingAddressFilters}
              onChange={setSelectedBillingAddressFilters}
              placeholder="Filter by Billing Address"
              searchPlaceholder="Search Billing Address"
              leadingIcon={<TuneIcon sx={{ fontSize: 17 }} />}
            />
          </div>
          <button
            type="button"
            onClick={clearFilters}
            className="ml-auto rounded-[8px] border-0 bg-[#F21B1B] px-[26px] py-[12px] text-[14px] leading-[normal] font-bold text-white hover:bg-[#D01414]"
          >
            Clear All Filter
          </button>
        </div>
      </ManagePageSection>

      {!chamberId && (
        <Alert severity="info" className="mt-5">
          Loading your {noun.singular} details. Your members will show in a moment.
        </Alert>
      )}

      {manageMembersError && (
        <Alert
          severity="error"
          className="mt-5"
          action={
            <Button
              color="inherit"
              size="small"
              disabled={manageMembersLoading}
              onClick={() => {
                void refetchManageMembers().catch(() => undefined);
              }}
            >
              Retry
            </Button>
          }
        >
          {getUserFacingErrorText(
            manageMembersError,
            `We could not load your member list. Click Retry. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`,
          )}
        </Alert>
      )}

      <ManagePageSection
        className="mt-[24px]"
        title="Member Lists"
        actions={<MassMemberCredentials chamberId={chamberId || ""} chamberName={chamber?.name || ""} />}
        headerRowClassName="flex flex-col gap-[18px] sm:flex-row sm:items-center sm:justify-between"
        titleClassName="text-[17px] font-bold leading-[normal] text-[#0A0A0A]"
        description="View membership details."
        descriptionClassName="mt-[6px] text-[14px] leading-[normal] text-black"
      >
        <DataTable
          ariaLabel="manage members table"
          columns={columns}
          rows={members}
          getRowKey={(member) => member.id}
          sort={{
            column: sortColumn || "actions",
            direction: sortDirection,
            // Only sortable columns render sort labels, so the shared
            // table never requests a sort on the "actions" key.
            onRequestSort: (column) => requestSort(column as SortableColumnKey),
          }}
          rowClassName=""
          containerClassName="mt-[24px] [container-type:inline-size] overflow-auto rounded-t-[10px] border border-[#ECECEC]"
          tableSx={{
            minWidth: 1250,
            display: "block",
            width: "100%",
            "& thead, & tbody": { display: "block" },
            "& .MuiTableRow-root": {
              display: "grid",
              gridTemplateColumns:
                "minmax(170px,1.2fr) minmax(120px,.9fr) minmax(115px,.8fr) minmax(140px,1fr) minmax(125px,.9fr) minmax(125px,.9fr) minmax(125px,.9fr) 190px",
              alignItems: "center",
              borderBottom: "1px solid #ECECEC",
            },
            "& .MuiTableCell-root[colspan]": {
              gridColumn: "1 / -1",
              position: "sticky",
              left: 0,
              width: "100cqw",
              boxSizing: "border-box",
            },
            "& .MuiTableCell-root": {
              padding: "16px 14px",
              fontSize: 12,
              borderBottom: 0,
              minWidth: 0,
              lineHeight: "normal",
              color: "#0A0A0A",
            },
            "& .MuiTableHead-root .MuiTableCell-root": {
              padding: "14px",
              fontSize: 13,
              fontWeight: 700,
              backgroundColor: "#F7F7F8",
            },
          }}
          renderSortLabel={({
            column,
            label,
            active,
            direction,
            onRequestSort,
          }) => (
            <button
              type="button"
              onClick={() => onRequestSort(column)}
              className="flex w-full items-center gap-[6px] border-0 bg-transparent p-0 text-left text-[13px] font-semibold text-[#0A0A0A]"
            >
              {label}
              <svg aria-hidden="true" width="9" height="14" viewBox="0 0 9 14">
                <path
                  d="m4.5 0 4 5h-8Z"
                  fill={active && direction === "asc" ? "#E1A731" : "#C4C7CC"}
                />
                <path
                  d="m4.5 14-4-5h8Z"
                  fill={active && direction === "desc" ? "#E1A731" : "#C4C7CC"}
                />
              </svg>
            </button>
          )}
          emptyRowContent={
            <TableEmptyState
              heading="No members match your filters"
              onClear={clearFilters}
            />
          }
          showLoadingRow={manageMembersLoading && allMembers.length === 0}
          showEmptyRow={
            !manageMembersLoading && !manageMembersError && members.length === 0
          }
        />
        <div className="rounded-b-[10px] border border-t-0 border-[#ECECEC] px-[14px] py-[12px] text-[13px] text-[#0A0A0A]">
          {manageMembersError && !manageMembersData
            ? "We could not load your member list. Click Retry above."
            : members.length
            ? `Showing all ${members.length} members`
            : "Showing 0 of 0 members"}
        </div>
      </ManagePageSection>

      <DesignDialog
        open={Boolean(memberToArchive)}
        onClose={() => setMemberToArchive(null)}
        busy={archivingMember}
        icon="trash"
        title="Delete Member"
        beforeMessage={
          deleteAutoRenewWarning && memberToArchive ? (
            <AutoRenewDeleteWarning company={memberToArchive.companyName} />
          ) : undefined
        }
        message={
          <DeleteMemberMessage
            company={memberToArchive?.companyName || ""}
            statusModel={memberStatusModel}
          />
        }
        actions={
          <>
            <DesignDialogButton
              tone="red"
              disabled={archivingMember || !!deleteBlockedReason}
              onClick={() => void handleArchiveConfirm()}
            >
              Delete
            </DesignDialogButton>
            <DesignDialogButton
              tone="dark"
              disabled={archivingMember}
              onClick={() => setMemberToArchive(null)}
            >
              Cancel
            </DesignDialogButton>
          </>
        }
      >
        <MemberStatusReasonField
          kind="delete"
          value={archiveReason}
          error={archiveReasonError}
          disabled={archivingMember}
          onChange={(value) => {
            setArchiveReason(value);
            setArchiveReasonError(false);
          }}
        />
        {deleteBlockedReason && (
          <p role="alert" className="mt-3 text-left">
            <DeleteMemberBlockedReason reason={deleteBlockedReason} />
          </p>
        )}
        {archiveError && (
          <p role="alert" className="mt-3 text-sm text-red-600">
            {archiveError}
          </p>
        )}
      </DesignDialog>

      <MemberActionSuccessDialog
        onClose={() => {
          if (!archiveRefreshLoading) {
            setArchiveSuccessOpen(false);
          }
        }}
        state={
          archiveSuccessOpen
            ? {
                title: "",
                tone: "green",
                body: (
                  <>
                    <p>
                      <strong>{archivedMemberName}</strong> has been deleted.
                      All information is stored as a Past Member.
                    </p>
                    <MemberPastTransitionPaymentSurfaceFeedback
                      feedback={archivePaymentSurfaceFeedback}
                      className="text-left"
                    />
                  </>
                ),
                actionLoading: archiveRefreshLoading,
                refreshWarning: archiveRefreshWarning,
                secondaryActionLabel: archiveRefreshWarning
                  ? "Refresh Member List"
                  : undefined,
                onSecondaryAction: archiveRefreshWarning
                  ? () => {
                      void handleRetryArchiveRefresh();
                    }
                  : undefined,
              }
            : null
        }
      />
    </ManagePageShell>
  );
};

export default ManageMembers;
